import { type ReactNode, useSyncExternalStore } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { env } from '../../../shared/config/env';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../shared/ui/accordion';
import { Card, CardContent, CardHeader, CardTitle } from '../../../shared/ui/card';

const WIDE = '(min-width: 1024px)';

/** Whether the screen is wide enough for the notices to sit beside the upload. Wide where it cannot be asked. */
function useIsWide(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      if (typeof window.matchMedia !== 'function') return () => {};
      const query = window.matchMedia(WIDE);
      query.addEventListener('change', onChange);
      return () => query.removeEventListener('change', onChange);
    },
    () => (typeof window.matchMedia === 'function' ? window.matchMedia(WIDE).matches : true),
  );
}

/**
 * What a customer is told before they upload a script: that it stays private and secure and can be removed, and
 * why it is asked for so early. Always in full view on a wide screen; on a phone the two notices are an accordion
 * that starts open, so they can be folded away but are never missed.
 */
export function ScriptUploadNotices() {
  const { t } = useTranslation('order');
  const wide = useIsWide();
  const privacy = (
    <div className="grid gap-3 text-sm">
      <p className="font-bold text-heading">{t('script.privacy.lead')}</p>
      <div className="grid gap-1">
        <h4 className="font-bold text-heading">{t('script.privacy.privateTitle')}</h4>
        <p>
          <Trans
            t={t}
            i18nKey="script.privacy.privateBody"
            components={{
              policy: (
                // biome-ignore lint/a11y/useAnchorContent: the catalogue text is placed inside by <Trans>
                <a
                  href={env.privacyPolicyUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="font-bold text-primary underline underline-offset-2"
                />
              ),
            }}
          />
        </p>
      </div>
      <div className="grid gap-1">
        <h4 className="font-bold text-heading">{t('script.privacy.secureTitle')}</h4>
        <p>{t('script.privacy.secureBody')}</p>
      </div>
      <p>{t('script.privacy.removal')}</p>
    </div>
  );
  const why = (
    <div className="grid gap-3 text-sm">
      <p>{t('script.why.onlyScript')}</p>
      <p>{t('script.why.entities')}</p>
      <p>{t('script.why.early')}</p>
    </div>
  );
  const notices: { key: string; title: string; body: ReactNode }[] = [
    { key: 'privacy', title: t('script.privacy.title'), body: privacy },
    { key: 'why', title: t('script.why.title'), body: why },
  ];

  if (wide) {
    return (
      <div className="grid gap-4">
        {notices.map((notice) => (
          <Card key={notice.key} size="compact">
            <CardHeader>
              <CardTitle asChild className="font-sans">
                <h3>{notice.title}</h3>
              </CardTitle>
            </CardHeader>
            <CardContent>{notice.body}</CardContent>
          </Card>
        ))}
      </div>
    );
  }
  return (
    <Accordion
      type="multiple"
      defaultValue={notices.map((n) => n.key)}
      className="rounded-lg border bg-card px-4"
    >
      {notices.map((notice) => (
        <AccordionItem key={notice.key} value={notice.key}>
          <AccordionTrigger>{notice.title}</AccordionTrigger>
          <AccordionContent>{notice.body}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
